import { createStore, applyMiddleware } from 'redux-4';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import thunk from 'redux-thunk-4';
import { isEnv } from '../../environment';
import { ENVS } from '../../constants';
import { diyReducer, diyState } from './diy';
import { autoData, autoDataReduccer } from './autoData';

function isFunction(x) {
  return Object.prototype.toString.call(x) === '[object Function]';
}

const logger = store => next => action => {
  const prevState = store.getState();
  next(action);
  const nextState = store.getState();

  const isThunk = isFunction(action);
  const log1 = isThunk ? 'THUNK' : 'ACTION';
  const log2 = isThunk ? action.name || action.constructor.name : action.type;

  try {
    /* eslint-disable no-console */
    console.log(`%c[MOVEIQ_${log1} - ${log2} -`, 'color: #0091EA', {
      action,
      prevState,
      nextState,
    });
    /* eslint-enable */
  } catch (e) {} // eslint-disable-line no-empty
};

const defaultState = {
  homeSize: null,
  loadingEligibility: true,
  currentMoveInit: false,
  moveId: null,
  progress: 0,
  date: null,
  fromAddress: {
    city: '',
    state: '',
    postalCode: '',
    street: '',
    unit: '',
  },
  toAddress: {
    city: '',
    state: '',
    postalCode: '',
    street: '',
    unit: '',
  },
  eligible: false,
  moveType: null,
  user: {
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    uuid: '',
  },
  prices: false,
  diyFsmChoice: undefined,
  companiesForEstimates: null,
  companiesForEstimatesLoaded: false,
  clientMoveIqMove: null,
  surveyConfig: 'noLogos',
  hasViewedEstimateConfirmation: false,
  sessionHeaders: {},
  diy: diyState,
  autoData,
};

const combineReducers = (state, action) => (...reducers) =>
  reducers.reduce((acc, reducer) => acc || reducer(state, action), undefined);

function moveIqReducer(state = defaultState, action) {
  // TODO: move all reducers into Redux's combineReducer pattern
  // For now this allows me to start seperating some reducers without rewriting
  const result = combineReducers(state, action)(diyReducer, autoDataReduccer);
  if (result) {
    return result;
  }
  switch (action.type) {
    case 'CHANGE_HOME_SIZE':
      return { ...state, homeSize: action.homeSize };
    case 'CHANGE_MOVE_DATE':
      return { ...state, date: action.date };
    case 'INIT_FULL_ADDRESS':
      return {
        ...state,
        fromAddress: action.fromAddress,
        toAddress: action.toAddress,
      };
    case 'INIT_ELIGIBILITY':
      return {
        ...state,
        eligible: action.eligible,
        moveType: action.moveType,
        loadingEligibility: false,
      };
    case 'UPDATE_PROGRESS':
      return {
        ...state,
        progress: action.progress,
      };
    case 'UPDATE_DIY_FSM_CHOICE':
      return {
        ...state,
        diyFsmChoice: action.diyFsmChoice,
      };
    case 'INIT_CURRENT_MOVE':
      return {
        ...state,
        currentMoveInit: true,
      };
    case 'INIT_USER':
      return {
        ...state,
        user: action.user,
      };
    case 'INIT_BALLPARK_PRICES':
      return {
        ...state,
        prices: action.prices,
      };
    case 'INIT_COMPANIES_FOR_ESTIMATES':
      return {
        ...state,
        companiesForEstimates: action.companiesForEstimates,
        companiesForEstimatesLoaded: true,
      };
    case 'SET_CLIENT_MOVEIQ_MOVE':
      return {
        ...state,
        clientMoveIqMove: action.clientMoveIqMove,
      };
    case 'SET_SURVEY_CONFIG':
      return {
        ...state,
        surveyConfig: action.surveyConfig,
      };
    case 'UPDATE_REMOTE_ESTIMATES': {
      return {
        ...state,
        remoteEstimates: action.remoteEstimates,
      };
    }
    case 'SET_HAS_VIEWED_ESTIMATE_CONFIRMATION': {
      return {
        ...state,
        hasViewedEstimateConfirmation: action.hasViewedEstimateConfirmation,
      };
    }
    case 'SET_SESSION_HEADERS': {
      return {
        ...state,
        sessionHeaders: action.sessionHeaders,
      };
    }
    default:
      return state;
  }
}

export const initFullAddress = ({ fromAddress, toAddress }) => ({
  type: 'INIT_FULL_ADDRESS',
  fromAddress,
  toAddress,
});

export const initUser = user => ({
  type: 'INIT_USER',
  user,
});

export const changeHomeSize = homeSize => ({
  type: 'CHANGE_HOME_SIZE',
  homeSize,
});

export const changeMoveDate = date => ({
  type: 'CHANGE_MOVE_DATE',
  date,
});

export const updateProgress = progress => ({
  type: 'UPDATE_PROGRESS',
  progress,
});

export const updateDiyFsmChoice = diyFsmChoice => ({
  type: 'UPDATE_DIY_FSM_CHOICE',
  diyFsmChoice,
});

export const setClientMoveIqMove = clientMoveIqMove => ({
  type: 'SET_CLIENT_MOVEIQ_MOVE',
  clientMoveIqMove,
});

export const setSurveyConfig = surveyConfig => ({
  type: 'SET_SURVEY_CONFIG',
  surveyConfig,
});

export const setHasViewedEstimateConfirmation = hasViewedEstimateConfirmation => ({
  type: 'SET_HAS_VIEWED_ESTIMATE_CONFIRMATION',
  hasViewedEstimateConfirmation,
});

export const setSessionHeaders = sessionHeaders => ({
  type: 'SET_SESSION_HEADERS',
  sessionHeaders,
});

const isDevelop = isEnv(ENVS.DEVELOPMENT, ENVS.TEST);

const middleware = [...(isDevelop ? [logger] : []), thunk];

const created = {};

let store;
let persistorInstance;
let currentMoveId;

export const createReduxStore = id => {
  currentMoveId = id;
  if (created[id]) {
    return;
  }
  defaultState.moveId = id;
  const persistConfig = {
    key: `upd-moveiq-redux-${id}`,
    whitelist: [
      'homeSize',
      'eligible',
      'moveType',
      'diyFsmChoice',
      'clientMoveIqMove',
      'surveyConfig',
      'diy',
      ...(isDevelop
        ? ['companiesForEstimates', 'companiesForEstimatesLoaded']
        : []),
    ],
    storage,
  };
  store = createStore(
    persistReducer(persistConfig, moveIqReducer),
    applyMiddleware(...middleware)
  );
  persistorInstance = persistStore(store);
  created[id] = {
    store,
    persistorInstance,
  };
};

export const persistor = () => created[currentMoveId].persistorInstance;

export const reduxStore = () => created[currentMoveId].store;

export * from './eligibility';
export * from './currentMove';
export * from './ballparkPrices';
export * from './companiesForEstimates';
export * from './diy';
